/** Shared helpers for the Store Returns admin page. */

export const RETURN_REASON_LABELS = {
  DAMAGED_OR_DEFECTIVE: 'Damaged or defective',
  NOT_AS_DESCRIBED: 'Not as described',
  WRONG_ITEM: 'Wrong item',
  CHANGED_MIND: 'Changed mind',
  OTHER: 'Other',
};

export const ESCALATION_LABELS = {
  NO_STORE_RESPONSE: 'Store did not respond in time',
  BUYER_DISPUTE: 'Buyer disputed the store\'s rejection',
};

export const RETURN_STATUS_LABELS = {
  REQUESTED: 'Requested',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  ESCALATED: 'Escalated',
  REFUNDED: 'Refunded',
  CANCELLED: 'Cancelled',
};

export const formatMoney = (amount, currency) => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return '—';
  return `${Number(amount).toFixed(2)} ${currency || ''}`.trim();
};

export const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

/** A money input: positive digits with at most two decimals (no signs, exponents or separators). */
export const MONEY_PATTERN = /^\d+(\.\d{1,2})?$/;

/** True when `amount` is the whole refundable balance (cent tolerance). */
export const isFullAmount = (amount, refundable) => Math.abs(Number(amount) - Number(refundable)) < 0.005;

/**
 * Turns a failed decide call into an admin-readable message.
 *
 *  - code REFUND_REJECTED (502): PayPal refused. Nothing moved and the return is open again. Only
 *    this code is a definite refusal.
 *  - any other 5xx (500, or a proxy's uncoded 502/503/504) or no status (timeout, dropped
 *    connection): the outcome is UNKNOWN, the refund may or may not have gone through. The refund
 *    is idempotent per return, so repeating the same decision is safe and will not refund twice.
 *  - 409 CONFLICT: when approving, another refund on the same order is in progress (retry
 *    shortly); when rejecting, the return was changed at the same time by someone else (refresh).
 *  - 400 typed codes: a rule the admin can act on.
 *
 * `decision` is 'approve' (default) or 'reject' so the copy only talks about refunds when money was
 * involved.
 *
 * Returns { message, retrySafe, stale, refresh, locked } — `locked` means the server may already
 * have applied the original decision, so a repeat settles THAT decision (a changed amount / note
 * would be ignored) and the dialog must not offer edits; `stale` means the list is out of date (someone
 * else already handled the return, or the refundable balance changed) so the dialog can only be
 * closed; `refresh` means the list should be reloaded when the dialog closes but the same call may
 * still be repeated.
 */
export const describeDecideError = (err, decision = 'approve') => {
  const rejecting = decision === 'reject';
  const status = err?.status;
  const code = err?.backendCode || '';
  const detail = err?.backendMessage || '';

  if (code === 'REFUND_REJECTED') {
    return {
      message: 'The payment provider refused the refund. Nothing was refunded and the return is still '
        + 'escalated. Check the amount and try again, or contact PayPal.'
        + (detail ? ` (${detail})` : ''),
      retrySafe: false,
      stale: false,
    };
  }
  if (status === undefined || status === null || status >= 500) {
    return {
      message: rejecting
        ? 'We could not confirm the decision (the server did not answer cleanly). Close this dialog to '
          + 'refresh the list and see the current state. Retrying is safe: press Confirm again to repeat '
          + 'the same decision.'
        : 'The refund outcome is unknown (the server did not answer cleanly): the refund may or may not '
          + 'have gone through. Retrying is safe: the same decision will not refund twice. '
          + 'Press Confirm again to retry.',
      retrySafe: true,
      stale: false,
      refresh: true,
      locked: true,
    };
  }
  if (status === 409 && (!code || code === 'CONFLICT') && rejecting) {
    return {
      message: 'This return was changed at the same time by someone else. Close this dialog to refresh '
        + 'the list, then try again.',
      retrySafe: false,
      stale: true,
      refresh: true,
    };
  }
  if (status === 409 && (!code || code === 'CONFLICT')) {
    return {
      message: 'Another refund on this order is in progress. Wait a moment and press Confirm again.',
      retrySafe: true,
      stale: false,
      refresh: true,
    };
  }
  switch (code) {
    case 'RETURN_REFUND_EXCEEDS_REMAINING':
      return {
        message: 'The refund exceeds what is left to refund on this order. The approval was undone and '
          + 'the return is open again. Close this dialog to refresh the list and the refundable balance.',
        retrySafe: false,
        stale: true,
      };
    case 'RETURN_ALREADY_HANDLED':
    case 'RETURN_INVALID_STATE':
      return {
        message: 'This return has already been decided or is no longer escalated. Close this dialog '
          + 'to refresh the list.',
        retrySafe: false,
        stale: true,
      };
    case 'RETURN_NOTE_REQUIRED':
      return { message: 'A note is required to reject a return.', retrySafe: false, stale: false };
    case 'PARTIAL_REFUND_NEEDS_REASON':
      return {
        message: 'A partial refund needs a note explaining why it is less than the full amount.',
        retrySafe: false,
        stale: false,
      };
    case 'INVALID_REFUND_AMOUNT':
      return {
        message: 'That refund amount is not valid. It must be above zero, with at most 2 decimals, and no more '
          + 'than the refundable balance. The return is still open.',
        retrySafe: false,
        stale: false,
        refresh: true,
      };
    case 'ORDER_REFUNDED':
      return {
        message: 'This order has already been fully refunded. Close this dialog to refresh the list.',
        retrySafe: false,
        stale: true,
      };
    default:
      break;
  }
  if (status === 403) return { message: 'You do not have permission to decide returns.', retrySafe: false, stale: false };
  if (status === 404) {
    return { message: 'This return was not found. Close this dialog to refresh the list.', retrySafe: false, stale: true };
  }
  return {
    message: detail || err?.message || 'The decision could not be saved.',
    retrySafe: false,
    stale: false,
  };
};
